/**
 * MelDispatchStatus Component
 * Displays a "Reference Only" indicator — not a dispatch decision.
 */

import React from 'react';
import { cn } from '@/utils/cn';
import type { DispatchStatus } from '@/types/mel';
import {
  BookOpenIcon,
} from '@heroicons/react/24/solid';

interface MelDispatchStatusProps {
  status: DispatchStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
}

const sizeConfig = {
  sm: {
    badge: 'px-2 py-0.5 text-xs',
    icon: 'w-3 h-3',
    gap: 'gap-1',
  },
  md: {
    badge: 'px-3 py-1 text-sm',
    icon: 'w-4 h-4',
    gap: 'gap-1.5',
  },
  lg: {
    badge: 'px-4 py-1.5 text-base',
    icon: 'w-5 h-5',
    gap: 'gap-2',
  },
};

export const MelDispatchStatus: React.FC<MelDispatchStatusProps> = ({
  status: _status,
  size = 'md',
  showIcon = true,
  showLabel = true,
  className,
}) => {
  const sizeStyles = sizeConfig[size];

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700',
        sizeStyles.badge,
        sizeStyles.gap,
        className
      )}
    >
      {showIcon && <BookOpenIcon className={sizeStyles.icon} />}
      {showLabel && <span>Reference Only</span>}
    </span>
  );
};

/**
 * Compact version for tight spaces
 */
export const MelDispatchStatusCompact: React.FC<{
  status: DispatchStatus;
  className?: string;
}> = ({ status: _status, className }) => {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded border',
        'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700',
        className
      )}
    >
      REF
    </span>
  );
};

export default MelDispatchStatus;
